import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { ClassPicker } from "@/components/ClassPicker";
import { PhotoUpload } from "@/components/PhotoUpload";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Image, Trash2 } from "lucide-react";

export default function GalleryPage() {
  const { user } = useAuth();
  const isParent = user?.role === "parent";
  const [scope, setScope] = useState({ branch_id: "", class_name: "", section: "" });
  const [rows, setRows] = useState([]);
  const [view, setView] = useState(null);
  const [f, setF] = useState({ caption: "", photo_url: "" });

  async function load() {
    const p = isParent ? {} : { branch_id: scope.branch_id, class_name: scope.class_name || undefined };
    setRows((await api.get("/gallery", { params: p })).data);
  }
  useEffect(() => { if (isParent || scope.branch_id) load().catch(() => {}); }, [scope.branch_id, scope.class_name]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    try { await api.post("/gallery", { ...scope, ...f }); toast.success("Photo added"); setF({ caption: "", photo_url: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <AppShell>
      <PageHeader eyebrow="Photos" title="Photo Gallery" subtitle="Moments from class activities and events." />
      {!isParent && <div className="mb-5"><ClassPicker value={scope} onChange={setScope} allowAllSections /></div>}
      {!isParent && (
        <div data-testid="photo-form" className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-3">
          <h3 className="font-display font-bold text-slate-900">Add photo{scope.class_name ? ` • ${scope.class_name}` : ""}</h3>
          {!scope.class_name ? <p data-testid="photo-pick-class" className="text-sm text-slate-500">Choose a class above to add photos.</p> : (
            <div className="grid sm:grid-cols-[auto_1fr] gap-4 items-end">
              <PhotoUpload value={f.photo_url} onChange={(v) => setF({ ...f, photo_url: v })} />
              <div className="space-y-3">
                <div><Label>Caption</Label><Input data-testid="photo-caption" value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} className="mt-1" /></div>
                <Button data-testid="photo-save" onClick={save} disabled={!f.photo_url} className="btn-brand">Add to gallery</Button>
              </div>
            </div>
          )}
        </div>
      )}
      {rows.length === 0 ? <Empty testid="gallery-empty" icon={Image} text="No photos yet." /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {rows.map((g) => (
            <div key={g.id} data-testid={`photo-${g.id}`} className="bg-white rounded-2xl overflow-hidden shadow-sm group relative">
              <button onClick={() => setView(g)} className="block w-full"><AuthImage src={g.photo_url} alt={g.caption} className="h-40 w-full object-cover" /></button>
              <div className="p-2.5">
                <div className="text-sm font-semibold text-slate-800 truncate">{g.caption || "Untitled"}</div>
                <div className="text-[11px] text-slate-500">{g.class_name} • {fmtDate(g.created_at)}</div>
              </div>
              {!isParent && <button data-testid={`delete-photo-${g.id}`} onClick={async () => { await api.delete(`/gallery/${g.id}`); load(); }} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 text-slate-500 hover:text-red-600"><Trash2 size={14} /></button>}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!view} onOpenChange={(v) => { if (!v) setView(null); }}>
        <DialogContent className="max-w-3xl">{view && <><AuthImage src={view.photo_url} alt={view.caption} className="w-full max-h-[70vh] object-contain rounded-xl" /><p className="text-sm text-slate-600">{view.caption}</p></>}</DialogContent>
      </Dialog>
    </AppShell>
  );
}
